<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import drawerMenu from './components/drawer.vue'
import Footer from './components/Footer.vue'
import Notification from './components/notification.vue'
import CookieConsent from './components/CookieConsent.vue'
import UpdateNotice from './components/UpdateNotice.vue'
import ConfirmDialog from './components/ConfirmDialog.vue'
import ResetPasswordDialog from './components/ResetPasswordDialog.vue'
import { provideNavRail } from './composables/useNavRail'
import { checkForUpdates } from './utils/version'

const route = useRoute()
const updateNotice = ref(null)
const { expanded, toggleNavRail } = provideNavRail()
const isOverlay = computed(() => route.path.startsWith('/overlay/'))
const hideChrome = computed(() => isOverlay.value || route.path === '/discord')
const showFooter = computed(() =>
  !hideChrome.value
  && !['/livemap', '/404'].includes(route.path)
  && !route.path.includes('leaderboard/'),
)
let updateTimer
let imageObserver

watch(isOverlay, (active) => {
  document.documentElement.classList.toggle('overlay-mode', active)
  document.body.classList.toggle('overlay-mode', active)
}, { immediate: true })

function bindImage(image) {
  if (!(image instanceof HTMLImageElement) || image.dataset.nrBound === '1') return
  image.dataset.nrBound = '1'
  image.dataset.nrImg = '1'
  const ready = () => image.classList.add('nr-img-ready')
  if (image.complete) ready()
  else {
    image.addEventListener('load', ready, { once: true })
    image.addEventListener('error', ready, { once: true })
  }
}

async function checkUpdates() {
  await checkForUpdates((info) => updateNotice.value?.showNotice(info))
}

onMounted(async () => {
  document.querySelectorAll('img').forEach(bindImage)
  imageObserver = new MutationObserver((records) => records.forEach((record) =>
    record.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return
      if (node.tagName === 'IMG') bindImage(node)
      node.querySelectorAll?.('img').forEach(bindImage)
    }),
  ))
  imageObserver.observe(document.body, { childList: true, subtree: true })
  await checkUpdates()
  updateTimer = window.setInterval(checkUpdates, 30 * 60 * 1000)
})

onBeforeUnmount(() => {
  imageObserver?.disconnect()
  window.clearInterval(updateTimer)
  document.documentElement.classList.remove('overlay-mode')
  document.body.classList.remove('overlay-mode')
})
</script>

<template>
  <v-card class="app-shell">
    <v-layout class="app-layout">
      <v-navigation-drawer
        v-if="!hideChrome"
        permanent
        :rail="false"
        :width="expanded ? 208 : 72"
        class="app-nav-rail"
        :class="{ 'is-expanded': expanded }"
      >
        <div class="nav-shell">
          <div class="nav-brand">
            <RouterLink to="/" class="nav-brand-link" aria-label="NightRiderz home">
              <span class="nav-brand-swap" aria-hidden="true">
                <span class="nav-brand-logo nav-brand-mark" :class="{ active: !expanded }" />
                <span class="nav-brand-logo nav-brand-wide" :class="{ active: expanded }" />
              </span>
            </RouterLink>
          </div>
          <drawerMenu />
        </div>
        <button
          type="button"
          class="nav-collapse-btn"
          :aria-label="expanded ? 'Collapse menu' : 'Expand menu'"
          :aria-expanded="expanded"
          @click="toggleNavRail"
        >
          <v-icon class="nav-collapse-icon" size="14">mdi-chevron-right</v-icon>
        </button>
      </v-navigation-drawer>

      <v-main>
        <Notification v-if="!isOverlay" />
        <ConfirmDialog v-if="!isOverlay" />
        <ResetPasswordDialog v-if="!isOverlay" />
        <router-view v-slot="{ Component, route: currentRoute }">
          <Transition name="page" mode="out-in">
            <div
              :key="currentRoute.matched.at(-1)?.path || currentRoute.name"
              class="page-view"
              :class="{ 'is-overlay-page': isOverlay }"
            >
              <component :is="Component" />
            </div>
          </Transition>
        </router-view>
        <Footer v-if="showFooter" :is-home="route.path === '/'" />
        <UpdateNotice v-if="!isOverlay" ref="updateNotice" />
        <CookieConsent v-if="!isOverlay" />
      </v-main>
    </v-layout>
  </v-card>
</template>

<style>
.app-shell { flex: 1; background: var(--secondary-color) !important; }
.app-layout { display: flex; min-height: 100vh; }
.app-nav-rail {
  --nav-ease: cubic-bezier(.16, 1, .3, 1);
  --nav-duration: .48s;
  position: relative;
  height: 100dvh !important;
  max-height: 100dvh !important;
  top: 0 !important;
  transition: width var(--nav-duration) var(--nav-ease) !important;
}
.app-nav-rail.v-navigation-drawer--active, .app-nav-rail.v-navigation-drawer { transition: width var(--nav-duration) var(--nav-ease) !important; }
.app-nav-rail .v-navigation-drawer__content { height: 100% !important; overflow: hidden !important; overscroll-behavior: none; }
.nav-shell { display: flex; flex-direction: column; height: 100%; overflow: hidden; }
.nav-shell .nav-rail { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
.nav-brand { flex-shrink: 0; display: flex; align-items: center; justify-content: flex-start; height: 64px; padding: 0 15px; border-bottom: 1px solid var(--line); box-sizing: border-box; }
.nav-brand-link { display: flex; align-items: center; justify-content: center; width: 100%; height: 44px; padding: 0; border-radius: 12px; overflow: hidden; transition: background .18s ease; }
.app-nav-rail.is-expanded .nav-brand-link { justify-content: flex-start; }
.nav-brand-link:hover { background: var(--hover-color); }
.nav-brand-swap { position: relative; display: block; width: 34px; height: 34px; transition: width var(--nav-duration) var(--nav-ease), height var(--nav-duration) var(--nav-ease); }
.app-nav-rail.is-expanded .nav-brand-swap { width: 100%; height: 22px; }
.nav-brand-logo { position: absolute; inset: 0; display: block; background-position: center; background-repeat: no-repeat; background-size: contain; opacity: 0; pointer-events: none; transition: opacity .24s ease, transform .32s var(--nav-ease); }
.nav-brand-mark { background-image: url('https://cdn.nightriderz.world/images/website/logo/NRZ_Normal.svg'); transform: scale(.92); }
.nav-brand-wide { background-image: url('https://cdn.nightriderz.world/images/website/logo/NRZ_Wide.svg'); background-position: left center; transform: translate(-8px); }
.nav-brand-mark.active { opacity: 1; transform: scale(1); transition-delay: .14s; }
.nav-brand-wide.active { opacity: 1; transform: translate(0); transition-delay: .16s; }
.app-nav-rail.is-expanded .nav-brand-mark { transition-delay: 0s; }
.app-nav-rail:not(.is-expanded) .nav-brand-wide { transition-delay: 0s; }
.nav-collapse-btn { position: absolute; top: 50%; right: 0; z-index: 2; display: flex; align-items: center; justify-content: center; width: 14px; height: 36px; margin: 0; padding: 0; transform: translateY(-50%); border: 1px solid var(--line); border-right: none; border-radius: 8px 0 0 8px; background: var(--nav-bg-top); color: var(--faint); font-size: inherit; font-weight: 400; letter-spacing: 0; line-height: 1; vertical-align: middle; cursor: pointer; opacity: .55; transition: opacity .18s ease, color .18s ease, background .18s ease, border-color .18s ease; }
.app-nav-rail:hover .nav-collapse-btn, .nav-collapse-btn:focus-visible { opacity: 1; }
.nav-collapse-btn:hover, .nav-collapse-btn:focus-visible { color: var(--ink-soft); background: var(--card-bg-solid); border-color: var(--line-strong); }
.nav-collapse-icon { transition: transform var(--nav-duration) var(--nav-ease); }
.app-nav-rail.is-expanded .nav-collapse-icon { transform: rotate(180deg); }
html.overlay-mode, html.overlay-mode body, html.overlay-mode #app { background: transparent !important; background-color: transparent !important; overflow: hidden !important; }
html.overlay-mode .v-main, body.overlay-mode .v-main { padding: 0 !important; --v-layout-left: 0px !important; background: transparent !important; }
html.overlay-mode .v-application, html.overlay-mode .v-application__wrap, html.overlay-mode .page-view, html.overlay-mode .app-shell, html.overlay-mode .v-card, html.overlay-mode .v-layout { min-height: 0 !important; background: transparent !important; background-color: transparent !important; box-shadow: none !important; }
.page-view.is-overlay-page { background: transparent !important; }
@media (max-width: 700px) {
.app-nav-rail { width: 64px !important; }
.app-nav-rail.is-expanded { width: min(208px, 78vw) !important; }

}
@media (prefers-reduced-motion: reduce) {
.app-nav-rail, .nav-brand-swap, .nav-brand-logo, .nav-collapse-icon { transition: none !important; }

}
</style>
